import { useMutation } from '@tanstack/react-query';
import { useWizardStore, type DeadlineDirection, type Privacy } from '../wizardStore';
import { subscriptionApi } from '@/entities/subscription/api';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { WizardFooter } from '@/shared/ui/WizardFooter';
import { GlassSelect } from '@/shared/ui/GlassSelect';
import { NumberInput } from '@/shared/ui/NumberInput';
import { useT } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

const PRIVACY_OPTIONS: { value: Privacy }[] = [
  { value: 'everyone' },
  { value: 'count_only' },
  { value: 'nobody' },
];

function privacyInfo(v: Privacy, t: ReturnType<typeof useT>) {
  const map: Record<Privacy, { label: string; desc: string }> = {
    everyone: { label: t('wizard.members.everyone'), desc: t('wizard.members.everyoneDesc') },
    count_only: { label: t('wizard.members.countOnly'), desc: t('wizard.members.countOnlyDesc') },
    nobody: { label: t('wizard.members.nobody'), desc: t('wizard.members.nobodyDesc') },
  };
  return map[v];
}

export function CollectStep() {
  const { deadlineMode, deadlineDays, deadlineDirection, privacy, showHistory, subscriptionId, update, next } = useWizardStore();

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!subscriptionId) { next(); return Promise.resolve(null); }
      return subscriptionApi.patch(subscriptionId, { privacy, show_history: showHistory, draft_step: 'split' });
    },
    onSuccess: (result) => { if (result !== null) next(); },
  });
  const t = useT();

  const DEADLINE_MODES = [
    { value: 'same' as const, label: t('wizard.price.deadlineSame') },
    { value: 'before' as const, label: t('wizard.price.deadlineBefore') },
    { value: 'custom' as const, label: t('wizard.price.deadlineCustom') },
  ];

  const DIRECTIONS: { value: DeadlineDirection; label: string }[] = [
    { value: 'before', label: t('wizard.price.deadlineBef') },
    { value: 'after', label: t('wizard.price.deadlineAft') },
  ];

  return (
    <div className="flex flex-col gap-4 flex-1">
      <h1 className="text-[24px] font-bold tracking-tight">{t('wizard.collect.title')}</h1>

      {/* Deadline */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[15px] font-medium text-text-primary">
            {t('wizard.collect.sentence')}
          </span>
          <GlassSelect
            value={deadlineMode}
            options={DEADLINE_MODES}
            onChange={(v) => update({ deadlineMode: v })}
            className="w-full"
          />
        </div>

        {deadlineMode === 'custom' && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] text-text-secondary">{t('wizard.price.deadlineDays')}</span>
            <NumberInput
              value={deadlineDays}
              onChange={(v) => update({ deadlineDays: v })}
              min={1}
              max={30}
              integer
              className="w-[66px] px-3 py-[9px] text-[18px] font-semibold text-center"
            />
            <div className="flex rounded-[10px] overflow-hidden border border-white/70 bg-white/50">
              {DIRECTIONS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => update({ deadlineDirection: d.value })}
                  className={cn(
                    'px-3 py-[7px] text-[13px] font-medium transition-all',
                    deadlineDirection === d.value
                      ? 'bg-brand-gradient text-white'
                      : 'text-text-secondary',
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <span className="text-[14px] text-text-secondary">{t('wizard.price.deadlineOf')}</span>
          </div>
        )}
      </div>

      {/* Privacy */}
      <div>
        <p className="text-[14px] font-semibold text-text-secondary mb-2">{t('wizard.members.privacyLabel')}</p>
        <div className="flex flex-col gap-2">
          {PRIVACY_OPTIONS.map(({ value }) => {
            const info = privacyInfo(value, t);
            return (
              <button
                key={value}
                className={cn(
                  'w-full rounded-[11px] px-4 py-[10px] text-left transition-all',
                  privacy === value
                    ? 'bg-brand-green/10 border-2 border-brand-green'
                    : 'bg-white/50 border border-white/60',
                )}
                onClick={() => update({ privacy: value })}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[15px] font-medium">{info.label}</span>
                  {value === 'count_only' && privacy !== 'count_only' && (
                    <span className="text-[11px] text-status-credit font-semibold">{t('wizard.members.default')}</span>
                  )}
                  {privacy === value && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0c9981" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </div>
                <p className="text-[12px] text-text-muted mt-0.5">{info.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Show history toggle */}
      <div
        className="flex items-center justify-between rounded-[13px] px-4 py-3 bg-white/50 border border-white/55 cursor-pointer"
        onClick={() => update({ showHistory: !showHistory })}
      >
        <span className="text-[14px]">{t('wizard.members.showHistory')}</span>
        <span
          className={cn(
            'w-[44px] h-[25px] rounded-full relative flex-shrink-0 transition-colors ml-3',
            showHistory ? 'bg-brand-gradient' : 'bg-black/14',
          )}
        >
          <span
            className={cn(
              'absolute top-[2px] w-[21px] h-[21px] rounded-full bg-white shadow-sm transition-all',
              showHistory ? 'left-[21px]' : 'left-[2px]',
            )}
          />
        </span>
      </div>

      <div className="flex-1" />

      <WizardFooter>
        {patchMutation.isError && (
          <p className="text-[13px] text-status-debt text-center">{t('wizard.preview.error')}</p>
        )}
        <PrimaryButton onClick={() => patchMutation.mutate()} loading={patchMutation.isPending}>
          {t('wizard.continue')}
        </PrimaryButton>
      </WizardFooter>
    </div>
  );
}
